/*
 * Etapes visuelles du dialog d'envoi du bilan par email.
 * FormStep : coordonnees et consentements separes.
 * CodeStep : code personnel local et lancement de l'envoi.
 * SentStep : confirmation avec rappel du code.
 * ErrorStep : echec avec reprise ou telechargement local.
 */

import { useEffect, useState } from "react";
import { Check, Copy, FileDown, Hourglass, KeyRound, Lock, Mail, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PersonalCodePanel } from "@/components/bilan/PersonalCodePanel";

const FALLBACK_TEXTS = {
  service: "J'accepte de recevoir mon bilan chiffr\u00e9 par email. Cette adresse est utilis\u00e9e pour cet envoi et pour le suivi commercial de ma demande.",
  marketing: "Je souhaite aussi recevoir les conseils et les actualit\u00e9s de Gino Body & Soul par email. Ce choix est facultatif et je peux me d\u00e9sinscrire \u00e0 tout moment.",
  phone: "J'accepte d'\u00eatre recontact\u00e9e par t\u00e9l\u00e9phone au sujet de mon parcours CYCLIQ. Ce choix est facultatif.",
};

const outlineButtonClass =
  "min-h-[48px] rounded-xl border-[rgba(35,82,119,0.3)] text-[#235277] hover:bg-[#E2F6FF] font-heading font-semibold transition-colors duration-200";
const primaryButtonClass =
  "min-h-[48px] rounded-xl bg-[#235277] hover:bg-[#1E4767] font-heading font-semibold transition-colors duration-200";

/* ----- Etape 1 : coordonnees et consentements separes ----- */

export const FormStep = ({ form, setForm, consentTexts, formError, onContinue }) => (
  <>
    <DialogHeader>
      <DialogTitle className="font-heading text-lg text-[#060606]">Recevoir mon bilan par email</DialogTitle>
      <DialogDescription className="text-sm text-[#333333]">
        Ton bilan sera envoyé en PDF protégé par un code personnel affiché à l'écran.
        Ce code n'est jamais envoyé : sans lui, le document reste illisible.
      </DialogDescription>
    </DialogHeader>
    <div className="space-y-4 pt-1">
      <div>
        <Label htmlFor="bilan-prenom" className="text-sm font-medium text-[#060606]">Prénom</Label>
        <Input
          id="bilan-prenom"
          data-testid="bilan-prenom-input"
          value={form.prenom}
          onChange={(e) => setForm({ prenom: e.target.value })}
          placeholder="Ton prénom"
          autoComplete="given-name"
          className="mt-1.5 min-h-[48px] rounded-xl border-[rgba(35,82,119,0.18)]"
        />
      </div>
      <div>
        <Label htmlFor="bilan-email" className="text-sm font-medium text-[#060606]">
          Adresse email <span className="text-[#D74843]">*</span>
        </Label>
        <Input
          id="bilan-email"
          type="email"
          data-testid="bilan-email-input"
          value={form.email}
          onChange={(e) => setForm({ email: e.target.value })}
          placeholder="ton@email.com"
          autoComplete="email"
          className="mt-1.5 min-h-[48px] rounded-xl border-[rgba(35,82,119,0.18)]"
        />
      </div>
      <div>
        <Label htmlFor="bilan-telephone" className="text-sm font-medium text-[#060606]">
          Téléphone <span className="text-[#9E9E9E] font-normal">(optionnel)</span>
        </Label>
        <Input
          id="bilan-telephone"
          type="tel"
          data-testid="bilan-phone-input"
          value={form.telephone}
          onChange={(e) => setForm({ telephone: e.target.value })}
          placeholder="+596 …"
          autoComplete="tel"
          className="mt-1.5 min-h-[48px] rounded-xl border-[rgba(35,82,119,0.18)]"
        />
      </div>

      <div className="flex items-start gap-3">
        <Checkbox
          id="bilan-consent-service"
          data-testid="bilan-service-consent-checkbox"
          checked={form.serviceConsent}
          onCheckedChange={(v) => setForm({ serviceConsent: Boolean(v) })}
          className="mt-0.5"
        />
        <label htmlFor="bilan-consent-service" className="text-xs leading-relaxed text-[#333333] cursor-pointer">
          {consentTexts.service || FALLBACK_TEXTS.service}
        </label>
      </div>
      <div className="flex items-start gap-3">
        <Checkbox
          id="bilan-consent-marketing"
          data-testid="bilan-marketing-consent-checkbox"
          checked={form.marketingConsent}
          onCheckedChange={(v) => setForm({ marketingConsent: Boolean(v) })}
          className="mt-0.5"
        />
        <label htmlFor="bilan-consent-marketing" className="text-xs leading-relaxed text-[#333333] cursor-pointer">
          <span className="font-semibold text-[#235277]">Facultatif : </span>
          {consentTexts.marketing || FALLBACK_TEXTS.marketing}
        </label>
      </div>
      {form.telephone.trim() && (
        <div className="flex items-start gap-3">
          <Checkbox
            id="bilan-consent-phone"
            data-testid="bilan-phone-consent-checkbox"
            checked={form.phoneConsent}
            onCheckedChange={(v) => setForm({ phoneConsent: Boolean(v) })}
            className="mt-0.5"
          />
          <label htmlFor="bilan-consent-phone" className="text-xs leading-relaxed text-[#333333] cursor-pointer">
            <span className="font-semibold text-[#235277]">Facultatif : </span>
            {consentTexts.phone || FALLBACK_TEXTS.phone}
          </label>
        </div>
      )}
      <p className="text-[11px] text-[#9E9E9E]">
        Refuser les communications facultatives ne change rien à la réception de ton bilan.
        Seules tes coordonnées et tes consentements sont transmis : jamais tes réponses ni tes scores.
      </p>
      {formError && (
        <p className="text-xs text-[#D74843]" role="alert" data-testid="bilan-email-form-error">{formError}</p>
      )}
      <Button onClick={onContinue} data-testid="bilan-email-continue-button" className={`w-full ${primaryButtonClass}`}>
        Continuer
      </Button>
    </div>
  </>
);

/* ----- Etape 2 : code personnel et envoi ----- */

export const CodeStep = ({ personalCode, sending, copied, onCopy, onDownload, onSend }) => (
  <>
    <DialogHeader>
      <DialogTitle className="font-heading text-lg text-[#060606] flex items-center gap-2">
        <KeyRound className="w-5 h-5 text-[#235277]" aria-hidden="true" />
        Ton code personnel
      </DialogTitle>
      <DialogDescription className="text-sm text-[#333333]">
        Ce code protège ton bilan PDF. Note le ou copie le maintenant : il ne te sera
        jamais renvoyé et CYCLIQ ne peut pas le retrouver.
      </DialogDescription>
    </DialogHeader>
    <div className="space-y-4 pt-1">
      <PersonalCodePanel code={personalCode} testId="bilan-personal-code" />
      <p className="text-xs leading-relaxed text-[#333333]">
        Ton bilan sera chiffré dans ton navigateur avec ce code avant d'être envoyé.
        L'email contiendra uniquement le PDF protégé : jamais le code.
      </p>
      <div className="flex flex-col gap-3">
        <Button onClick={onCopy} variant="outline" data-testid="bilan-copy-code-button" className={outlineButtonClass}>
          {copied ? <Check className="mr-2 w-4 h-4" aria-hidden="true" /> : <Copy className="mr-2 w-4 h-4" aria-hidden="true" />}
          Copier mon code personnel
        </Button>
        <Button onClick={onDownload} variant="outline" data-testid="bilan-download-now-button" className={outlineButtonClass}>
          <FileDown className="mr-2 w-4 h-4" aria-hidden="true" />
          Télécharger mon bilan maintenant
        </Button>
        <Button onClick={onSend} disabled={sending} data-testid="bilan-send-email-button" className={primaryButtonClass}>
          {sending ? (
            <>
              <Lock className="mr-2 w-4 h-4 animate-pulse" aria-hidden="true" />
              Chiffrement et envoi en cours…
            </>
          ) : (
            <>
              <Mail className="mr-2 w-4 h-4" aria-hidden="true" />
              Envoyer mon bilan par email
            </>
          )}
        </Button>
      </div>
    </div>
  </>
);

/* ----- Etape 3 : confirmation avec rappel du code ----- */

export const SentStep = ({ personalCode, copied, onCopy, onClose }) => (
  <>
    <DialogHeader>
      <DialogTitle className="font-heading text-lg text-[#060606] flex items-center gap-2">
        <Check className="w-5 h-5 text-[#114E0B]" aria-hidden="true" />
        Ton bilan est en route
      </DialogTitle>
      <DialogDescription className="text-sm text-[#333333]">
        Ton bilan chiffré arrive à l'adresse indiquée. Pour l'ouvrir, utilise ton code
        personnel ci dessous. Il ne te sera jamais renvoyé : garde le précieusement.
      </DialogDescription>
    </DialogHeader>
    <div className="space-y-4 pt-1">
      <PersonalCodePanel code={personalCode} testId="bilan-personal-code-reminder" />
      <Button onClick={onCopy} variant="outline" data-testid="bilan-copy-code-after-send-button" className={`w-full ${outlineButtonClass}`}>
        {copied ? <Check className="mr-2 w-4 h-4" aria-hidden="true" /> : <Copy className="mr-2 w-4 h-4" aria-hidden="true" />}
        Copier mon code personnel
      </Button>
      <Button onClick={onClose} data-testid="bilan-close-dialog-button" className={`w-full ${primaryButtonClass}`}>
        J'ai noté mon code, fermer
      </Button>
    </div>
  </>
);

/* ----- Etape 4 : quota atteint, patience douce avec compte a rebours ----- */

/* Format lisible du delai restant : heures et minutes, ou minutes et secondes. */
const formatDelay = (total) => {
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  if (hours > 0) return `${hours} h ${String(minutes).padStart(2, "0")} min`;
  if (minutes > 0) return `${minutes} min ${String(seconds).padStart(2, "0")} s`;
  return `${seconds} s`;
};

const QUOTA_MESSAGES = {
  ip_quota:
    "Plusieurs bilans sont déjà partis depuis ta connexion cette dernière heure. Pour protéger le service, un court délai s'applique avant le prochain envoi.",
  recipient_quota:
    "Cette adresse a déjà reçu plusieurs bilans aujourd'hui. Un délai s'applique avant de pouvoir lui renvoyer un bilan.",
};

export const QuotaStep = ({ personalCode, seconds, reason, onRetry, onDownload }) => {
  const [remaining, setRemaining] = useState(seconds);

  useEffect(() => {
    setRemaining(seconds);
    const timer = setInterval(
      () => setRemaining((prev) => (prev > 0 ? prev - 1 : 0)),
      1000,
    );
    return () => clearInterval(timer);
  }, [seconds]);

  const ready = remaining <= 0;

  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-heading text-lg text-[#060606] flex items-center gap-2">
          <Hourglass className="w-5 h-5 text-[#235277]" aria-hidden="true" />
          Petite pause avant le prochain envoi
        </DialogTitle>
        <DialogDescription className="text-sm text-[#333333]" data-testid="bilan-quota-message">
          {QUOTA_MESSAGES[reason] || QUOTA_MESSAGES.ip_quota}
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-4 pt-1" data-testid="bilan-quota-step">
        <div className="rounded-xl border border-[rgba(35,82,119,0.14)] bg-[rgba(226,246,255,0.45)] p-4 text-center">
          {ready ? (
            <p className="font-heading text-base font-semibold text-[#235277]" data-testid="bilan-quota-ready">
              C'est bon, tu peux relancer l'envoi.
            </p>
          ) : (
            <>
              <p className="text-[11px] font-medium uppercase tracking-wide text-[#9E9E9E]">
                Tu pourras réessayer dans
              </p>
              <p
                className="mt-1 font-heading text-3xl font-semibold tabular-nums text-[#235277]"
                data-testid="bilan-quota-countdown"
                aria-live="polite"
              >
                {formatDelay(remaining)}
              </p>
            </>
          )}
        </div>
        <p className="text-xs leading-relaxed text-[#333333]">
          Aucune attente pour le téléchargement : ton bilan est disponible
          immédiatement sur cet appareil, protégé par ton code personnel.
        </p>
        <PersonalCodePanel code={personalCode} testId="bilan-personal-code-quota" compact />
        <div className="flex flex-col gap-3">
          <Button onClick={onDownload} variant="outline" data-testid="bilan-quota-download-button" className={outlineButtonClass}>
            <FileDown className="mr-2 w-4 h-4" aria-hidden="true" />
            Télécharger mon bilan maintenant
          </Button>
          <Button
            onClick={onRetry}
            disabled={!ready}
            data-testid="bilan-quota-retry-button"
            className={primaryButtonClass}
          >
            <RefreshCw className="mr-2 w-4 h-4" aria-hidden="true" />
            {ready ? "Relancer l'envoi" : "Envoi disponible après la pause"}
          </Button>
        </div>
      </div>
    </>
  );
};

/* ----- Etape 5 : echec avec reprise ----- */

export const ErrorStep = ({ personalCode, onRetry, onDownload }) => (
  <>
    <DialogHeader>
      <DialogTitle className="font-heading text-lg text-[#060606]">L'envoi n'a pas abouti</DialogTitle>
      <DialogDescription className="text-sm text-[#333333]">
        Ton bilan n'a pas pu être envoyé pour le moment. Tu peux réessayer l'envoi ou
        télécharger ton bilan directement sur ton appareil.
      </DialogDescription>
    </DialogHeader>
    <div className="space-y-3 pt-1">
      <PersonalCodePanel code={personalCode} testId="bilan-personal-code-error" compact />
      <Button onClick={onRetry} data-testid="bilan-retry-send-button" className={`w-full ${primaryButtonClass}`}>
        <RefreshCw className="mr-2 w-4 h-4" aria-hidden="true" />
        Réessayer l'envoi
      </Button>
      <Button onClick={onDownload} variant="outline" data-testid="bilan-download-fallback-button" className={`w-full ${outlineButtonClass}`}>
        <FileDown className="mr-2 w-4 h-4" aria-hidden="true" />
        Télécharger mon bilan
      </Button>
    </div>
  </>
);
